/**
 * Mixin that auto-generates mobileFilterConfig and handleMobileFilterUpdate
 * from a simple mobileFilterDefs computed property.
 *
 * Usage in parent component:
 *
 * mixins: [hasMobileFilters],
 *
 * computed: {
 *   mobileFilterDefs() {
 *     return {
 *       dateRange: {
 *         type: 'date',
 *         model: 'dateRange',
 *         label: this.$t('date'),
 *       },
 *       shifts: {
 *         type: 'autocomplete',
 *         model: 'selectedShifts',
 *         label: this.$t('shifts'),
 *         items: this.availableShifts,
 *         multiple: true,
 *       },
 *       view: {
 *         type: 'dropdown',
 *         model: 'selectedLayout',
 *         label: this.$t('view'),
 *         items: this.showItems,
 *         itemText: 'text',
 *         itemValue: 'value',
 *         // transform: converts raw dropdown value back to object
 *         transform: (val) => this.showItems.find(i => i.value === val) || null,
 *       },
 *     };
 *   },
 * }
 *
 * Template:
 *   <mobile-filters :filters="mobileFilterConfig" @update:filter="handleMobileFilterUpdate" />
 *
 * Filter definition properties:
 *   - model (string, required):  Data property name (supports dot notation: 'card.type')
 *   - type (string):             'date' | 'autocomplete' | 'dropdown' | 'search' | 'text' | 'custom'
 *   - label (string):            Filter label
 *   - items (array):             Options for autocomplete/dropdown
 *   - multiple (boolean):        Multiple selection
 *   - visible (boolean):         Show/hide filter (default: true)
 *   - clearable (boolean):       Allow clearing (default: false)
 *   - chipText (string):         Override auto-generated chip text
 *   - transform (function):      Transform value before setting model (e.g. dropdown value → object)
 *   - onUpdate (function):       Callback after model is set
 *   - ...any other props are passed through to MobileFilters config
 */
export default {
  computed: {
    mobileFilterConfig() {
      const defs = this.mobileFilterDefs;
      if (!defs) return {};

      const config = {};
      for (const [key, def] of Object.entries(defs)) {
        const { model, transform, onUpdate, ...passthrough } = def;
        const value = this._mfGetValue(def);

        config[key] = {
          ...passthrough,
          value,
          chipText: def.chipText || this._mfChipText(def, value),
          visible: def.visible !== undefined ? def.visible : true,
          clearable: def.clearable !== undefined ? def.clearable : false,
        };
      }
      return config;
    },
  },
  methods: {
    handleMobileFilterUpdate({ key, value }) {
      const def = this.mobileFilterDefs?.[key];
      if (!def?.model) return;

      this._mfSetValue(def.model, def.transform ? def.transform(value) : value);
      if (def.onUpdate) def.onUpdate(value);
    },

    // Resolve model to current value
    _mfGetValue(def) {
      if (!def.model) return def.value;
      const val = def.model
        .split(".")
        .reduce((obj, key) => obj?.[key], this);

      // Dropdown with object model: extract itemValue for the control
      if (
        def.type === "dropdown" &&
        val &&
        typeof val === "object" &&
        def.itemValue
      ) {
        return val[def.itemValue] ?? null;
      }
      return val;
    },

    // Set model value (supports dot notation)
    _mfSetValue(model, value) {
      const parts = model.split(".");
      if (parts.length === 1) {
        this[model] = value;
      } else {
        const parent = parts
          .slice(0, -1)
          .reduce((obj, key) => obj?.[key], this);
        if (parent) this.$set(parent, parts[parts.length - 1], value);
      }
    },

    // Auto-generate chipText based on type and value
    _mfChipText(def, value) {
      const label = def.label || "";

      switch (def.type) {
        case "date":
          if (Array.isArray(value) && value.length === 2 && value[0]) {
            const f = (d) => {
              const dt = new Date(d);
              if (isNaN(dt)) return String(d);
              return `${dt.getDate().toString().padStart(2, "0")}.${(dt.getMonth() + 1)
                .toString()
                .padStart(2, "0")}.`;
            };
            return value[0] === value[1] ? f(value[0]) : `${f(value[0])} - ${f(value[1])}`;
          }
          return label;

        case "autocomplete":
          if (def.multiple && Array.isArray(value)) {
            return value.length > 0
              ? `${value.length} ${label.toLowerCase()}`
              : label;
          }
          if (def.returnObject && value && typeof value === "object") {
            return value[def.itemText || "text"] || label;
          }
          return value ? String(value) : label;

        case "dropdown": {
          // For object models, read display text from the actual model value
          if (def.model) {
            const modelVal = def.model
              .split(".")
              .reduce((obj, key) => obj?.[key], this);
            if (modelVal && typeof modelVal === "object") {
              return modelVal[def.itemText || "text"] || label;
            }
          }
          // Fallback: look up in items
          if (def.items && Array.isArray(def.items)) {
            const iv = def.itemValue || "value";
            const it = def.itemText || "text";
            const found = def.items.find(
              (i) => (typeof i === "object" ? i[iv] : i) === value
            );
            if (found)
              return typeof found === "object" ? found[it] : String(found);
          }
          return label;
        }

        case "search":
        case "text":
          return value || label;

        default:
          return label;
      }
    },
  },
};
